import { useState, useEffect } from "react";
import {
  Client,
  CreateClientRequest,
  UpdateClientRequest,
} from "@/types/client";
import { ClientService } from "@/services/clientService";

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all clients
  const loadClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const clientsData = await ClientService.getClients();
      setClients(clientsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  // Create a new client
  const createClient = async (
    clientData: CreateClientRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const newClient = await ClientService.createClient(clientData);
      setClients((prev) => [newClient, ...prev]);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create client");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update a client
  const updateClient = async (
    clientData: UpdateClientRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const updatedClient = await ClientService.updateClient(clientData);
      setClients((prev) =>
        prev.map((client) =>
          client.id === updatedClient.id ? updatedClient : client
        )
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update client");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete a client
  const deleteClient = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await ClientService.deleteClient(id);
      setClients((prev) => prev.filter((client) => client.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete client");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  return {
    clients,
    loading,
    error,
    loadClients,
    createClient,
    updateClient,
    deleteClient,
    setError,
  };
};
